import { OnboardingStepType, useMutationNotifier, useOrgDashboardQuery } from '@alice-financial/alice-sdk'
import {
  AssignUserToStepMutation,
  useAssignUserToStepMutation,
} from '@alice-financial/alice-sdk/src/graphql/organization/onboarding/mutations/assignUserToStep_gen'
import {
  ApiRootException,
  GqlInputErrorException,
  UseGqlMutationCallbacks,
  useMutationOptionsWithInvalidation,
} from '@alice-financial/api'
import { useMutation } from '@tanstack/react-query'
import { useOrgId } from '../useOrgId'

export type UseAssignUserToStepInput = {
  orgId: number
  userId: number
  stepType: OnboardingStepType
}

type UseAssignUserToStepOptions = UseGqlMutationCallbacks<AssignUserToStepMutation, UseAssignUserToStepInput>

export const useAssignUserToStep = (mutationOptions?: UseAssignUserToStepOptions) => {
  const orgId = useOrgId()
  const { mutateAsync: assignUserToStep } = useAssignUserToStepMutation()

  const _mutationOptions = useMutationNotifier(
    {
      onMutate: 'Assigning user...',
      onSuccess: 'User assigned successfully',
      onError: 'Failed to assign user',
    },
    mutationOptions
  )

  // Invalidate organization data when assignment changes - step status will update to 'complete'
  const invalidationKeys = [useOrgDashboardQuery.getKey({ orgId })]

  const mutationOptionsWithInvalidation = useMutationOptionsWithInvalidation(
    _mutationOptions,
    invalidationKeys
  )

  return useMutation<
    AssignUserToStepMutation,
    ApiRootException | GqlInputErrorException<UseAssignUserToStepInput>,
    UseAssignUserToStepInput
  >(
    ['assignUserToStep'],
    ({ orgId, userId, stepType }) =>
      assignUserToStep({
        input: {
          orgId,
          userId,
          stepType,
        },
      }),
    mutationOptionsWithInvalidation
  )
}
